import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, User, Home, FileText, MapPin, Calendar, Upload, Trash2, StickyNote, 
  Download, Loader2, CheckCircle2, Award, Banknote, Layers, Clock, CalendarPlus,
  Euro, TrendingUp, Hammer, Eye, ListChecks, Info, BarChart3
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getUserRole } from "@/pages/Login";
import { buildTimelinesForProgram, TimelineDisplay } from "@/components/ProjectTimeline";
import { 
  formatCurrency, formatDateTime, PROFIL_LABELS, PRECA_LABELS, GESTES_LABELS, 
  EQUIP_LABELS, DOCS_PROFIL, DOCS_PROG, STATUS_OPTS, REGIE_THRESHOLD, getRegies,
  calcAutoQuantites174, getScenarios174, calc174, calcMPR, COUT_UNITAIRE
} from "@/utils/simulateurUtils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const STATUS_COLORS = {
  nouveau: "bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30",
  en_cours: "bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30",
  termine: "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  archive: "bg-muted/50 text-gray-700 border-gray-200",
};
const STATUS_LABELS = {
  nouveau: "Nouveau", en_cours: "En cours", termine: "Terminé", archive: "Archivé",
};
const STEP_STATUS_ICON = {
  done: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100" },
  in_progress: { icon: Clock, color: "text-amber-600", bg: "bg-amber-100" },
  not_started: { icon: ListChecks, color: "text-muted-foreground", bg: "bg-muted" },
};

export default function DossierRegieDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [etude, setEtude] = useState(null);
  const [adminDossier, setAdminDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const currentUser = localStorage.getItem("label_energie_user") || "";

  useEffect(() => {
    fetchData();
    // Poll for updates every 30s
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchData = async () => {
    // Try etudes_regie first
    try {
      const res = await fetch(`${API}/etudes/${id}`);
      if (res.ok) {
        const d = await res.json();
        setEtude(d);
        // Try to find linked admin dossier
        if (d.admin_dossier_id) {
          try {
            const dRes = await fetch(`${API}/dossiers/${d.admin_dossier_id}`);
            if (dRes.ok) setAdminDossier(await dRes.json());
          } catch (err) {}
        }
        setLoading(false);
        return;
      }
    } catch (err) {}
    // Fallback: fetch from dossiers collection
    try {
      const res = await fetch(`${API}/dossiers/${id}`);
      if (res.ok) {
        const d = await res.json();
        setAdminDossier(d);
        // If linked to etude, fetch for simulation data
        if (d.etude_id) {
          try {
            const eRes = await fetch(`${API}/etudes/${d.etude_id}`);
            if (eRes.ok) setEtude(await eRes.json());
          } catch (err) {}
        } else {
          // Try to find matching etude by source (creator) + type=dossier
          let foundEtude = false;
          const source = d.assigned_to || d.created_by || currentUser;
          try {
            const etudesRes = await fetch(`${API}/etudes?source=${encodeURIComponent(source)}&doc_type=dossier`);
            if (etudesRes.ok) {
              const allEtudes = await etudesRes.json();
              // Find etude matching client name
              const clientName = (d.client_name || "").toLowerCase().trim();
              if (clientName) {
                const match = allEtudes.find(e => 
                  (e.beneficiaire?.nom || "").toLowerCase().trim() === clientName
                );
                if (match) {
                  // Fetch full etude data
                  const fullRes = await fetch(`${API}/etudes/${match.id}`);
                  if (fullRes.ok) {
                    const fullEtude = await fullRes.json();
                    setEtude(fullEtude);
                    // Link dossier to etude for future lookups
                    fetch(`${API}/dossiers/${id}`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ etude_id: match.id }),
                    }).catch(() => {});
                    foundEtude = true;
                  }
                }
              }
            }
          } catch (err) {}
          
          if (!foundEtude) {
            // Build minimal etude from dossier data
            setEtude({
              id: d.id,
              beneficiaire: { nom: d.client_name, telephone: d.client_phone, email: d.client_email, adresse: d.adresse_logement },
              profil: d.profile, zone: d.zone, surface: d.metrage, precarite: d.precarite,
              recommendation: d.programme, created_at: d.created_at, source: d.assigned_to || d.created_by,
            });
          }
        }
        setLoading(false);
        return;
      }
    } catch (err) {}
    setLoading(false);
  };

  // Commission calculation (must be before any conditional returns)
  const isSCIMulti = adminDossier?.is_sci_multi === true;
  const commission = useMemo(() => {
    // SCI multi-logement: use user's commission_sci % or default 10%
    if (isSCIMulti) {
      const totalCA = adminDossier?.quote_amount || adminDossier?.total_aides || 0;
      if (totalCA > 0) {
        // Get user's SCI commission % from localStorage
        const userCommStr = localStorage.getItem("label_energie_commissions");
        let sciPct = 10; // default 10%
        if (userCommStr) {
          try {
            const uc = JSON.parse(userCommStr);
            sciPct = uc.commission_sci || 10;
          } catch (err) {}
        }
        const caHT = totalCA / 1.055;
        return Math.round(caHT * sciPct / 100);
      }
    }
    const prog = (adminDossier?.programme || etude?.recommendation || "").toLowerCase();
    // Try from etude regies first
    if (etude?.regies) {
      const regies = etude?.regies;
      if (prog === "174") return regies.r174 || 0;
      if (prog === "mpr") return regies.rMPR || 0;
      if (prog === "cumule" || prog === "cumulée") return (regies.r174Cum || 0) + (regies.rMPRCum || 0);
    }
    // Try sim_regies from dossier (stored at creation from simulation)
    const simRegies = adminDossier?.sim_regies;
    if (simRegies) {
      if (prog === "174") return simRegies.r174 || 0;
      if (prog === "mpr") return simRegies.rMPR || 0;
      if (prog === "cumule" || prog === "cumulée") return (simRegies.r174Cum || 0) + (simRegies.rMPRCum || 0);
    }
    // Fallback: compute from user's commission config using getRegies
    const surface = adminDossier?.metrage || etude?.surface || 80;
    const precarite = (adminDossier?.precarite || etude?.precarite || "bleu").toLowerCase();
    const fallbackRegies = getRegies("regie", precarite === "jaune" ? "jaune" : "bleu", surface);
    if (prog === "174") return fallbackRegies.r174 || 0;
    if (prog === "mpr") return fallbackRegies.rMPR || 0;
    if (prog === "cumule" || prog === "cumulée") return (fallbackRegies.r174Cum || 0) + (fallbackRegies.rMPRCum || 0);
    return 0;
  }, [etude, adminDossier, isSCIMulti]);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Chargement...</div>;
  if (!etude && !adminDossier) return <div className="p-8 text-center text-red-500">Dossier non trouvé</div>;

  const b = (etude?.beneficiaire) || {};
  const dossier = adminDossier;
  const programme = dossier?.programme || etude?.recommendation || "";
  const is174 = programme?.toLowerCase() === "174";
  const isMPR = programme?.toLowerCase() === "mpr";
  const isCumule = programme?.toLowerCase() === "cumule" || programme?.toLowerCase() === "cumulée";
  const statut = dossier?.statut_dossier || etude?.status || "nouveau";
  const steps = dossier?.steps || [];

  // Steps progress
  const doneSteps = steps.filter(s => s.status === "done").length;
  const totalSteps = steps.length;
  const progressPct = totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0;

  // Simulation data for Résultat tab
  const THRESHOLD = REGIE_THRESHOLD;
  const regies = etude?.regies || {};
  const res174 = etude?.results174 || [];
  const resMPR = etude?.allMPRResults || [];
  const a174 = etude?.active174Result;
  const aMPR = etude?.activeMPRResult;
  const a174C = etude?.active174CumResult;
  const aMPRC = etude?.activeMPRCumResult;

  let regie174OK = etude?.elig174?.eligible && a174 && a174.marge >= THRESHOLD;
  let regieMPROK = etude?.eligMPR?.eligible && aMPR && aMPR.marge >= THRESHOLD;
  let regieCumOK = etude?.eligCumule?.eligible && a174C && aMPRC && ((a174C.marge || 0) + (aMPRC.marge || 0)) >= THRESHOLD;

  const regieOpts = [];
  if (regie174OK) regieOpts.push({ type: "174", label: "Programme 174", earnings: regies.r174 || 0, margin: a174.marge });
  if (regieMPROK) regieOpts.push({ type: "mpr", label: `Programme MPR (${etude?.activeMPRType || ""})`, earnings: regies.rMPR || 0, margin: aMPR.marge });
  if (regieCumOK) regieOpts.push({ type: "cumule", label: `Op. Cumulée`, earnings: (regies.r174Cum || 0) + (regies.rMPRCum || 0), margin: (a174C?.marge || 0) + (aMPRC?.marge || 0) });

  // ═══ FALLBACK: Compute simulation from dossier data when no etude ═══
  let fallbackScenarios174 = [];
  let fallbackMPRResults = [];
  if (regieOpts.length === 0 && dossier) {
    const surface = dossier.metrage || etude?.surface || 80;
    const zone = dossier.zone || etude?.zone || "H1";
    const prec = (dossier.precarite || etude?.precarite || "bleu").toLowerCase();
    const chauffage = etude?.chauffage || "Gaz";
    const typeBat = (dossier.type_logements || etude?.typeBatiment || "maison").toLowerCase();
    const isMaison = typeBat === "maison" || typeBat === "maisons";
    const isJaune = prec === "jaune";
    const gestesDeja = etude?.gestesDeja || [];
    const fallbackRegies = getRegies("regie", isJaune ? "jaune" : "bleu", surface);
    const valorisation = 13;
    const couts = {};
    Object.keys(COUT_UNITAIRE).forEach(k => { couts[k] = { materiel: COUT_UNITAIRE[k].materiel, pose: COUT_UNITAIRE[k].pose }; });

    // 174: Trust programme field — if dossier programme includes 174 or cumule, it's eligible
    const prog174Eligible = is174 || isCumule || programme === "";
    if (prog174Eligible) {
      const qtes = calcAutoQuantites174(surface, isMaison ? "maison" : "appartement", "plain_pied", gestesDeja, chauffage);
      const scenarios = getScenarios174(qtes, isMaison, chauffage);
      scenarios.forEach(sc => {
        const result = calc174(surface, sc, valorisation, couts, qtes, fallbackRegies.r174, isJaune);
        fallbackScenarios174.push({ scenario: sc, result });
      });
      if (fallbackScenarios174.length > 0) {
        regieOpts.push({ type: "174", label: "Programme 174", earnings: fallbackRegies.r174, margin: fallbackScenarios174[0].result.marge });
      }
    }

    // MPR: maison + gaz/fioul, or programme is MPR/cumule
    const progMPREligible = isMPR || isCumule || (isMaison && (chauffage === "Gaz" || chauffage === "Fioul"));
    if (progMPREligible && prec !== "violet" && prec !== "rose" && isMaison) {
      const mprTypes = ["PAC", "PAC/SSC", "PAC + CESI"];
      mprTypes.forEach(tc => {
        const result = calcMPR(zone, surface, prec === "jaune" ? "jaune" : "bleu", tc, false, fallbackRegies.rMPR);
        fallbackMPRResults.push({ type: tc, result });
      });
      fallbackMPRResults.sort((a, b) => b.result.marge - a.result.marge);
      if (fallbackMPRResults.length > 0) {
        regieOpts.push({ type: "mpr", label: `Programme MPR (${fallbackMPRResults[0].type})`, earnings: fallbackRegies.rMPR, margin: fallbackMPRResults[0].result.marge });
      }
    }

    // Cumule: both available
    if (fallbackScenarios174.length > 0 && fallbackMPRResults.length > 0) {
      const cumMarge = fallbackScenarios174[0].result.marge + fallbackMPRResults[0].result.marge;
      regieOpts.push({ type: "cumule", label: "Op. Cumulée", earnings: (fallbackRegies.r174Cum || 0) + (fallbackRegies.rMPRCum || 0), margin: cumMarge });
    }
  }

  regieOpts.sort((a, b) => b.margin - a.margin);
  const regieBest = regieOpts[0]?.type || null;
  const regie174Sc = regie174OK ? res174.filter(r => r.result.marge >= THRESHOLD) : (fallbackScenarios174.length > 0 ? fallbackScenarios174 : []);
  const regieMPRSc = regieMPROK ? resMPR.filter(r => r.result.marge >= THRESHOLD) : (fallbackMPRResults.length > 0 ? fallbackMPRResults : []);

  const pBoxes = etude?.paymentBoxes || [];
  const buildTL = (detail) => buildTimelinesForProgram({ programType: detail, eligCumule: etude?.eligCumule?.eligible ? { order: etude.eligCumule.order } : null, elig174: etude?.elig174 || {}, eligMPR: etude?.eligMPR || {} });
  const regieTL = regieBest ? buildTL(regieBest) : { timelines: [], totalDays: 0 };

  // Notes handling
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    // Always use /dossiers/ endpoint — backend handles both collections
    const dosId = adminDossier?.id || id;
    try {
      const res = await fetch(`${API}/dossiers/${dosId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: noteText, author: currentUser }),
      });
      if (res.ok) { setNoteText(""); fetchData(); toast.success("Note ajoutée"); }
      else toast.error("Erreur lors de l'ajout");
    } catch (err) { toast.error("Erreur"); }
  };

  // File upload (own files only)
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const dosId = adminDossier?.id || id;
    const endpoint = `${API}/dossiers/${dosId}/files?uploaded_by=${encodeURIComponent(currentUser)}`;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(endpoint, { method: "POST", body: fd });
      if (res.ok) { setTimeout(() => fetchData(), 500); toast.success("Fichier uploadé"); }
    } catch (err) { toast.error("Erreur d'upload"); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Parse notes from all sources
  const allNotes = (() => {
    const notes = [];
    // From notes_list (structured, from /dossiers/:id/notes endpoint)
    const adNotes = adminDossier?.notes_list;
    if (Array.isArray(adNotes)) adNotes.forEach(n => notes.push({ ...n, source: "dossier" }));
    const etNotes = etude?.notes_list;
    if (Array.isArray(etNotes)) etNotes.forEach(n => notes.push({ ...n, source: "etude" }));
    // From etude.notes array (old format)
    const etOldNotes = etude?.notes;
    if (Array.isArray(etOldNotes)) {
      etOldNotes.forEach(n => {
        if (n.id && !notes.find(x => x.id === n.id)) {
          notes.push({ ...n, content: n.content || n.text, source: "etude" });
        }
      });
    }
    // Parse string notes only if no structured notes exist
    if (notes.length === 0) {
      const notesStr = typeof (adminDossier?.notes) === "string" ? adminDossier.notes : (typeof (etude?.notes_text) === "string" ? etude.notes_text : "");
      if (notesStr && notesStr.trim()) {
        notesStr.split("\n\n").filter(n => n.trim()).forEach((block, i) => {
          const match = block.match(/^\[(.+?) — (.+?)\]\n([\s\S]*)$/);
          if (match) {
            // Parse "18/03/2026 21:17" format to ISO
            const parts = match[1].match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
            const isoDate = parts ? `${parts[3]}-${parts[2]}-${parts[1]}T${parts[4]}:${parts[5]}:00` : "";
            notes.push({ id: `str-${i}`, created_at: isoDate, author: match[2], content: match[3] });
          } else {
            notes.push({ id: `str-${i}`, content: block, author: "", created_at: "" });
          }
        });
      }
    }
    return notes.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() || 0 : 0;
      const db2 = b.created_at ? new Date(b.created_at).getTime() || 0 : 0;
      return db2 - da;
    });
  })();

  // Files: from ALL sources, filtered by current user
  const allFilesSources = [
    ...(adminDossier?.files || []),
    ...(etude?.files || []),
    ...(etude?.documents || []),
  ];
  const ownFiles = allFilesSources.filter(f => 
    f.uploaded_by === currentUser || f.author === currentUser
  );

  // Get note author role label
  const getNoteAuthorLabel = (author) => {
    if (!author) return "";
    const lower = author.toLowerCase();
    if (lower === "renova") return "(Renova)";
    if (lower === "admin") return "(Admin)";
    return `(${getUserRole() === "regie" ? "Régie" : "Télépro"})`;
  };

  const statusColor = STATUS_COLORS[statut] || STATUS_COLORS.nouveau;
  const statusLabel = STATUS_LABELS[statut] || statut;
  const programmeLabel = isCumule ? "Op. Cumulée (174 + MPR)" : is174 ? "Programme 174" : isMPR ? `Programme MPR` : programme;

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-6xl mx-auto" data-testid="dossier-regie-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-muted" data-testid="btn-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-heading font-extrabold tracking-tight">{b.nom || "Dossier"}</h1>
            <span className={cn("px-3 py-1 rounded-full text-xs font-bold border", statusColor)} data-testid="dossier-status">
              {statusLabel}
            </span>
            <span className={cn(
              "px-2 py-1 text-xs font-bold rounded-sm border",
              is174 ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" :
              isMPR ? "bg-blue-500/10 text-blue-700 border-blue-500/30" :
              "bg-violet-500/10 text-violet-700 border-violet-500/30"
            )}>
              {programmeLabel}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            #{(adminDossier?.id || etude?.id || "").slice(-6)} — {formatDateTime(adminDossier?.created_at || etude?.created_at)}
          </p>
          {dossier?.assigned_to && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Attribué à : <span className="font-semibold">{dossier?.assigned_to}</span>
            </p>
          )}
        </div>
      </div>

      {/* Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Box 1: Étapes */}
        <div className="border border-border rounded-sm bg-card p-5" data-testid="box-etapes">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-blue-500" />
              Étapes
            </h2>
            <span className="text-xl font-bold tabular-nums">{doneSteps}/{totalSteps}</span>
          </div>
          {totalSteps > 0 ? (
            <>
              <div className="w-full bg-muted rounded-full h-2 mb-2">
                <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">{progressPct}% complété</p>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Aucune étape définie</p>
          )}
        </div>

        {/* Box 2: Commission */}
        {(() => {
          // Check if commission payment step is done
          const payeStep = steps.find(s => s.key === "paye" || s.key === "prime_cee");
          const isCommissionPaid = payeStep?.status === "done";
          const commLabel = commission > 0 
            ? (isCommissionPaid ? "Commission réalisée" : "Commission à venir")
            : "Commission non calculée";
          return (
        <div className={cn(
          "border rounded-sm p-5",
          commission > 0 && isCommissionPaid ? "border-emerald-500/30 bg-emerald-500/10" :
          commission > 0 ? "border-emerald-500/30 bg-emerald-500/10" : "border-border bg-card"
        )} data-testid="box-commission">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <Euro className={cn("w-5 h-5", commission > 0 && isCommissionPaid ? "text-emerald-600" : commission > 0 ? "text-[hsl(var(--renova-green))]" : "text-muted-foreground")} />
              {commission > 0 ? (isCommissionPaid ? "Commission réalisée" : "Commission à venir") : "Commission"}
            </h2>
            <span className={cn("text-xl font-bold tabular-nums", commission > 0 && isCommissionPaid ? "text-emerald-600" : commission > 0 ? "text-[hsl(var(--renova-green))]" : "text-muted-foreground")}>
              {commission > 0 ? formatCurrency(commission) : "—"}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {commLabel}
          </p>
        </div>
          );
        })()}

        {/* Box 3: Programme travaux */}
        <div className={cn(
          "border rounded-sm p-5",
          is174 ? "border-emerald-500/30 bg-emerald-500/10" :
          isMPR ? "border-blue-500/30 bg-blue-500/10" :
          isCumule ? "border-violet-500/30 bg-violet-500/10" :
          "border-border bg-card"
        )} data-testid="box-programme">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <Hammer className={cn("w-5 h-5", 
                is174 ? "text-emerald-600" : isMPR ? "text-blue-600" : isCumule ? "text-violet-600" : "text-muted-foreground"
              )} />
              Programme
            </h2>
          </div>
          <p className={cn("text-sm font-bold", 
            is174 ? "text-emerald-700" : isMPR ? "text-blue-700" : "text-violet-700"
          )}>
            {programmeLabel}
          </p>
          {dossier?.profile && (
            <p className="text-xs text-muted-foreground mt-1">
              Profil: {PROFIL_LABELS[dossier.profile] || dossier.profile}
            </p>
          )}
          {(dossier?.nombre_logements || 1) > 1 && (
            <p className="text-xs text-muted-foreground">
              {dossier.nombre_logements} logements — {dossier.nom_residence || ""}
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="etapes" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="etapes" data-testid="tab-etapes">Étapes</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="fichiers" data-testid="tab-fichiers">Fichiers</TabsTrigger>
          <TabsTrigger value="infos" data-testid="tab-infos">Informations</TabsTrigger>
          <TabsTrigger value="simulation" data-testid="tab-simulation">Résultat simulation</TabsTrigger>
        </TabsList>

        {/* Tab 1: Étapes (read-only) */}
        <TabsContent value="etapes" className="mt-4">
          {isSCIMulti && dossier?.logements?.length > 0 ? (
            /* SCI Multi: étapes par logement, read-only */
            <div className="space-y-3">
              <div className="border border-border rounded-sm bg-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Progression globale</span>
                  <span className="text-sm font-bold">{Math.round(dossier.steps_progress || 0)}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${dossier.steps_progress || 0}%` }} />
                </div>
              </div>
              {(() => {
                const grouped = {};
                dossier.logements.forEach(l => { const a = l.building_adresse || "—"; if (!grouped[a]) grouped[a] = []; grouped[a].push(l); });
                return Object.entries(grouped).map(([addr, logs]) => (
                  <div key={addr} className="border border-border rounded-sm overflow-hidden">
                    <div className="p-3 bg-muted/30 border-b border-border flex items-center gap-2">
                      <Home className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-sm">{addr}</span>
                    </div>
                    {logs.map(log => {
                      const lsteps = log.steps || [];
                      const done = lsteps.filter(s => s.status === "done").length;
                      const pct = lsteps.length > 0 ? Math.round(done / lsteps.length * 100) : 0;
                      return (
                        <div key={log.id} className="border-b border-border/50 last:border-0">
                          <div className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">{log.num}</span>
                              <span className="text-sm font-semibold">{log.nom_occupant}</span>
                              <span className="text-[10px] text-muted-foreground">{log.surface}m² · {log.type_logement}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-muted rounded-full h-1.5"><div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} /></div>
                              <span className="text-xs font-bold">{done}/{lsteps.length}</span>
                            </div>
                          </div>
                          <div className="px-3 pb-3 space-y-1">
                            {lsteps.map((step, si) => {
                              const scfg = STEP_STATUS_ICON[step.status] || STEP_STATUS_ICON.not_started;
                              const SIcon = scfg.icon;
                              return (
                                <div key={step.key || si} className={cn("flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs",
                                  step.status === "done" ? "bg-emerald-500/10" : step.status === "in_progress" ? "bg-amber-500/10" : "")}>
                                  <SIcon className={cn("w-3.5 h-3.5", scfg.color)} />
                                  <span className="flex-1">{step.name}</span>
                                  <span className={cn("text-[10px] font-semibold",
                                    step.status === "done" ? "text-emerald-600" : step.status === "in_progress" ? "text-amber-600" : "text-muted-foreground"
                                  )}>{step.status === "done" ? "Fait" : step.status === "in_progress" ? "En cours" : "—"}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          ) : steps.length === 0 ? (
            <div className="border border-border rounded-sm p-8 text-center bg-card">
              <ListChecks className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Aucune étape définie par l'administrateur</p>
            </div>
          ) : (
            <div className="space-y-2">
              {steps.map((step, i) => {
                const cfg = STEP_STATUS_ICON[step.status] || STEP_STATUS_ICON.not_started;
                const Icon = cfg.icon;
                return (
                  <div key={step.key || i} className={cn(
                    "flex items-center gap-3 p-3 rounded-sm border",
                    step.status === "done" ? "border-emerald-500/30 bg-emerald-500/10" :
                    step.status === "in_progress" ? "border-amber-500/30 bg-amber-500/10" :
                    "border-border bg-card"
                  )} data-testid={`step-${step.key}`}>
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", cfg.bg)}>
                      <Icon className={cn("w-4 h-4", cfg.color)} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{step.name || step.key}</p>
                      {step.date && <p className="text-xs text-muted-foreground">{step.date}</p>}
                    </div>
                    <span className={cn(
                      "px-2 py-1 rounded-sm text-xs font-semibold",
                      step.status === "done" ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" :
                      step.status === "in_progress" ? "bg-amber-500/20 text-amber-600 dark:text-amber-400" :
                      "bg-muted text-muted-foreground"
                    )}>
                      {step.status === "done" ? "Fait" : step.status === "in_progress" ? "En cours" : "Non démarré"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tab 2: Documents (read-only validation) */}
        <TabsContent value="documents" className="mt-4">
          <div className="border border-border rounded-sm bg-card p-5">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-blue-500" />Documents du dossier
            </h3>
            {(dossier?.documents || []).length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucun document</p>
            ) : (
              <div className="space-y-2">
                {(dossier?.documents || []).map((doc, i) => (
                  <div key={doc.id || i} className="flex items-center justify-between p-3 rounded-sm border border-border bg-muted/20">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{doc.name || doc.filename || "Document"}</p>
                        {doc.date && <p className="text-xs text-muted-foreground">{doc.date}</p>}
                      </div>
                    </div>
                    <span className={cn(
                      "px-2 py-1 rounded-sm text-xs font-semibold",
                      doc.status === "validated" || doc.status === "done" ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" :
                      doc.status === "pending" ? "bg-amber-500/20 text-amber-600 dark:text-amber-400" :
                      "bg-muted text-muted-foreground"
                    )}>
                      {doc.status === "validated" || doc.status === "done" ? "Validé" : 
                       doc.status === "pending" ? "En attente" : "Non validé"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab 3: Fichiers (own files only, upload only) */}
        <TabsContent value="fichiers" className="mt-4">
          <div className="border border-border rounded-sm bg-card p-5">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Upload className="w-4 h-4 text-blue-500" />Mes fichiers
            </h3>
            <div className="flex items-center gap-3 mb-4">
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" onChange={handleUpload} className="hidden" />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="rounded-sm" data-testid="btn-upload-file">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Upload...</> : <><Upload className="w-4 h-4 mr-2" />Ajouter un fichier</>}
              </Button>
            </div>
            {ownFiles.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucun fichier déposé</p>
            ) : (
              <div className="space-y-2">
                {ownFiles.map((file, i) => (
                  <div key={file.id || i} className="flex items-center justify-between p-3 rounded-sm border border-border bg-muted/20">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs font-semibold">{file.filename || file.name}</p>
                        <p className="text-[10px] text-muted-foreground">{formatDateTime(file.uploaded_at || file.created_at)}</p>
                      </div>
                    </div>
                    <a href={`${API}/dossiers/${adminDossier?.id || id}/files/${file.id}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-muted">
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes section — in Fichiers tab */}
          <div className="border border-border rounded-sm bg-card p-5 mt-4">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-amber-500" />Notes du dossier
            </h3>
            <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto">
              {allNotes.length === 0 && <p className="text-xs text-muted-foreground italic">Aucune note</p>}
              {allNotes.map((note, i) => (
                <div key={note.id || i} className="p-2 rounded-sm border border-border bg-muted/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold">{note.author || "Inconnu"}</span>
                    <span className="text-[10px] text-muted-foreground">{note.created_at ? (() => { const d = new Date(note.created_at); return isNaN(d.getTime()) ? note.created_at : d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); })() : ""}</span>
                  </div>
                  <p className="text-xs">{note.content || note.text}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Ajouter une note..."
                className="flex-1 px-3 py-2 rounded-sm border border-border bg-background text-sm"
                data-testid="input-note-fichiers"
              />
              <Button size="sm" disabled={!noteText.trim()} onClick={handleAddNote} className="rounded-sm">
                Ajouter
              </Button>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="infos" className="mt-4 space-y-4">
          {/* SCI info for multi-logement */}
          {isSCIMulti && dossier?.sci_info && (
            <div className="border border-border rounded-sm bg-card p-5">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <Award className="w-4 h-4 text-violet-500" />Informations SCI
              </h3>
              <div className="space-y-1.5">
                <InfoRow label="Nom SCI" value={dossier.sci_info.nom_sci || dossier.client_name} />
                <InfoRow label="Contact" value={dossier.sci_info.nom_contact || dossier.nom_representant} />
                <InfoRow label="Téléphone" value={dossier.sci_info.tel_contact || dossier.tel_representant} />
                <InfoRow label="Email" value={dossier.sci_info.email || dossier.client_email} />
                <InfoRow label="Adresse SCI" value={dossier.sci_info.adresse_sci || dossier.adresse_bailleur} />
              </div>
            </div>
          )}

          {/* Logements list for SCI multi */}
          {isSCIMulti && dossier?.logements?.length > 0 && (
            <div className="border border-border rounded-sm bg-card p-5">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
                <Home className="w-4 h-4 text-emerald-500" />{dossier?.logements?.length} Logements
              </h3>
              <div className="space-y-2">
                {dossier?.logements?.map((log, li) => (
                  <div key={log.id || li} className="flex items-center justify-between p-3 rounded-sm border border-border bg-muted/20">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">{log.num}</span>
                      <div>
                        <p className="text-xs font-semibold">{log.nom_occupant}</p>
                        <p className="text-[10px] text-muted-foreground">{log.building_adresse} · {log.surface}m² · {log.type_logement} · {log.chauffage} · DPE {log.dpe}</p>
                      </div>
                    </div>
                    <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-bold",
                      log.programme === "cumule" ? "bg-violet-500/20 text-violet-600 dark:text-violet-400" : "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                    )}>{log.programme === "cumule" ? "Cumulée" : "174"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Standard beneficiary info (non-SCI) */}
          {!isSCIMulti && (
          <div className="border border-border rounded-sm bg-card p-5">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-blue-500" />Bénéficiaire
            </h3>
            <div className="space-y-1.5">
              <InfoRow label="Nom" value={b.nom} />
              <InfoRow label="Téléphone" value={b.telephone} />
              <InfoRow label="Email" value={b.email} />
              <InfoRow label="Adresse" value={b.adresse} />
            </div>
          </div>
          )}

          {!isSCIMulti && (
          <div className="border border-border rounded-sm bg-card p-5">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Home className="w-4 h-4 text-blue-500" />Profil & Logement
            </h3>
            <div className="space-y-1.5">
              <InfoRow label="Profil" value={PROFIL_LABELS[etude?.profil] || etude?.profil} />
              <InfoRow label="Surface" value={etude?.surface ? `${etude.surface} m²` : ""} />
              <InfoRow label="Zone" value={etude?.zone || dossier?.zone} />
              <InfoRow label="Précarité" value={PRECA_LABELS[etude?.precarite] || etude?.precarite || dossier?.precarite} />
              <InfoRow label="Chauffage" value={etude?.chauffage} />
            </div>
          </div>
          )}

          {/* Notes */}
          <div className="border border-border rounded-sm bg-card p-5">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-blue-500" />Notes
            </h3>
            <div className="flex gap-2 mb-4">
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                className="flex-1 px-3 py-2 rounded-sm border border-border bg-background text-sm resize-none" 
                rows={2} placeholder="Ajouter une note..." data-testid="input-note" />
              <Button size="sm" onClick={handleAddNote} disabled={!noteText.trim()} 
                className="rounded-sm self-end" data-testid="btn-add-note">Ajouter</Button>
            </div>
            {allNotes.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucune note</p>
            ) : (
              <div className="space-y-2">
                {allNotes.map((note, i) => {
                  const isOwnNote = note.author === currentUser;
                  return (
                    <div key={note.id || i} className={cn(
                      "p-3 rounded-sm border",
                      isOwnNote ? "border-blue-500/30 bg-blue-500/10" : "border-border bg-muted/30"
                    )}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-muted-foreground">
                          {note.author} {getNoteAuthorLabel(note.author)} — {formatDateTime(note.created_at)}
                        </span>
                      </div>
                      <p className="text-xs">{note.content}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab 5: Résultat simulation */}
        <TabsContent value="simulation" className="mt-4 space-y-5">
          {regieOpts.length === 0 ? (
            <div className="border-2 border-amber-300 rounded-sm bg-amber-500/10 p-6 text-center">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <p className="font-bold text-amber-700">Données de simulation non disponibles</p>
              <p className="text-xs text-amber-600 mt-1">La simulation sera affichée si le dossier est lié à une étude</p>
            </div>
          ) : (
            <>
              {/* Programmes éligibles */}
              <div className="border-2 border-blue-500/30 rounded-sm bg-blue-500/10 p-5">
                <h3 className="font-semibold text-sm flex items-center gap-2 mb-4">
                  <Award className="w-4 h-4 text-blue-600" />Programmes éligibles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {regieOpts.map((opt, i) => {
                    // SCI multi: show total commission (10% HT) instead of per-logement
                    const displayEarnings = isSCIMulti ? commission : opt.earnings;
                    return (
                    <div key={opt.type} className={cn(
                      "p-4 rounded-sm border-2 bg-card shadow-sm",
                      i === 0 ? "border-emerald-400 ring-2 ring-emerald-200" : "border-border"
                    )}>
                      {i === 0 && <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Recommandé</p>}
                      <p className="font-semibold text-sm">{opt.label}</p>
                      <p className="text-lg font-extrabold text-[hsl(var(--renova-green))] mt-2">{formatCurrency(displayEarnings)}</p>
                      <p className="text-[10px] text-muted-foreground">Commission{isSCIMulti ? " totale" : ""}</p>
                    </div>
                    );
                  })}
                </div>

                {/* Scénarios MPR */}
                {regieMPRSc.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-blue-500/30">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Scénarios MPR :</p>
                    <div className="flex gap-2 flex-wrap">
                      {regieMPRSc.map(r => (
                        <span key={r.type} className="px-3 py-1.5 rounded-sm bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold border border-blue-500/30">
                          {r.type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scénarios 174 */}
                {regie174Sc.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-500/30">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Scénarios 174 :</p>
                    <div className="space-y-2">
                      {regie174Sc.map((r, i) => (
                        <div key={i} className="px-3 py-2 rounded-sm bg-emerald-500/10 border border-emerald-500/30">
                          <span className="text-xs font-bold text-emerald-700">{r.scenario.label}</span>
                          <p className="text-[10px] text-emerald-600 mt-0.5">
                            {r.scenario.sauts} sauts — {(r.scenario.gestes || []).map(g => GESTES_LABELS[g] || g).join(" + ")}
                            {(r.scenario.equipements || []).length > 0 && ` + ${r.scenario.equipements.map(e => EQUIP_LABELS[e] || e).join(", ")}`}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Timeline */}
              {regieTL.timelines.length > 0 && <TimelineDisplay timelines={regieTL.timelines} totalDays={regieTL.totalDays} />}

              {/* Payment boxes */}
              {pBoxes.length > 0 && (
                <div className={cn("grid gap-3", pBoxes.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 max-w-sm")}>
                  {pBoxes.map((box, i) => (
                    <div key={i} className={cn(
                      "rounded-sm border-2 p-4 text-center shadow-sm",
                      box.prog === "174" ? "border-emerald-500/40 bg-emerald-500/10" : "border-blue-500/40 bg-blue-500/10"
                    )}>
                      <Banknote className={cn("w-7 h-7 mx-auto mb-1.5", box.prog === "174" ? "text-emerald-600" : "text-blue-600")} />
                      <p className={cn("text-[10px] font-bold uppercase", box.prog === "174" ? "text-emerald-700" : "text-blue-700")}>{box.label}</p>
                      <p className={cn("text-2xl font-extrabold", box.prog === "174" ? "text-emerald-700" : "text-blue-700")}>{formatCurrency(box.amount)}</p>
                      <p className="text-xs font-semibold text-muted-foreground mt-1">J{box.day}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* SCI Multi: détail travaux par logement */}
              {isSCIMulti && dossier?.logements?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Layers className="w-4 h-4 text-violet-600" />Détail par logement
                  </h3>
                  {dossier?.logements?.map((log, li) => (
                    <div key={log.id || li} className="border border-border rounded-sm overflow-hidden">
                      <div className="p-3 bg-muted/20 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">{log.num}</span>
                          <span className="text-sm font-semibold">{log.nom_occupant}</span>
                          <span className="text-[10px] text-muted-foreground">{log.surface}m² · {log.type_logement} · {log.building_adresse}</span>
                        </div>
                        <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-bold",
                          log.programme === "cumule" ? "bg-violet-500/20 text-violet-600 dark:text-violet-400" : "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                        )}>{log.programme === "cumule" ? "Cumulée" : "174"}</span>
                      </div>
                      {log.travaux_detail?.length > 0 && (
                        <div className="divide-y divide-border/50">
                          {log.travaux_detail.map((t, ti) => (
                            <div key={ti} className="flex items-center justify-between px-4 py-1.5 text-xs">
                              <span>{t.label}</span>
                              <span className="text-muted-foreground">{t.qty} {t.unit}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {log.simulation && (
                        <div className="px-4 py-2 bg-emerald-500/10 border-t border-emerald-500/30 text-xs">
                          <span className="font-bold">{log.simulation.scenario || "—"}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-border/30">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-semibold">{value || "—"}</span>
    </div>
  );
}
